import React from "react";
import styled from "styled-components";
import { Form, Input, Row, Select, FormInstance, Tooltip, InputNumber } from "antd";
import fontStyle from "src/styles/fontStyle";
import { ProductStockInfo } from "@api/throughShipment/onlineOrderApi";
import { InfoCircleTwoTone } from "@ant-design/icons";

const Notice = styled.span`
  margin-top: 5px;
  margin-left: 10px;
  color: ${({ theme }) => theme.colorNeutral500};
  ${fontStyle("14px", "22px")};
`;

const InfoIcon = styled(InfoCircleTwoTone)`
  margin-left: 4px;
`;

type Props = {
  form: FormInstance;
  storageType: string;
  effectiveDateFormValue: string;
  matchingStorage: Array<{ key: string; value: string; name: string }>;
  stocksForEffectiveDate: ProductStockInfo[];
  stocksForBatch: ProductStockInfo[];
  handleBatchChange: (batch: string) => void;
  isB2B: boolean;
};

export default function RentrapWarehouseFields({
  form,
  storageType,
  effectiveDateFormValue,
  matchingStorage,
  stocksForEffectiveDate,
  stocksForBatch,
  handleBatchChange,
  isB2B,
}: Props) {
  return (
    <>
      <Row>
        <Form.Item label="barcode" name="barcode" labelAlign="left" rules={[{ required: true, message: "" }]}>
          <Input disabled />
        </Form.Item>
        <Notice>*輸入品號後自動帶入</Notice>
      </Row>

      <Row>
        <Form.Item label="標準價" name="standardPrice" labelAlign="left">
          <Input disabled />
        </Form.Item>
      </Row>

      <Form.Item
        shouldUpdate
        label="倉別"
        name="storageType"
        labelAlign="left"
        rules={[{ required: true, message: "" }]}
      >
        <Select
          style={{ width: "230px" }}
          onChange={() =>
            form.setFieldsValue({
              effectiveDate: undefined,
              batch: undefined,
              stock: undefined,
            })
          }
        >
          {matchingStorage.map((storage) => (
            <Select.Option key={storage.value} value={storage.value}>
              {storage.value}
              {storage.name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item label="商品效期" name="effectiveDate" labelAlign="left" rules={[{ required: true, message: "" }]}>
        <Select placeholder="請先選擇倉別" disabled={!storageType}>
          {stocksForEffectiveDate.map((stockInfo) => {
            return (
              <Select.Option
                key={stockInfo.effectiveDate || "no-effectiveDate"}
                value={stockInfo.effectiveDate || "no-effectiveDate"}
              >
                {stockInfo.effectiveDate ? stockInfo.effectiveDate : "無效期限制"}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>

      <Form.Item label="批號" name="batch" labelAlign="left">
        <Select onChange={(e) => handleBatchChange(e)} placeholder="請先選擇效期" disabled={!effectiveDateFormValue}>
          {stocksForBatch.map((stockInfo) => {
            return (
              <Select.Option key={stockInfo.batch} value={stockInfo.batch}>
                {stockInfo.batch ? stockInfo.batch : "無批號"}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>

      <Row>
        <Form.Item label="庫存量" name="stock" labelAlign="left">
          <Input disabled />
        </Form.Item>
      </Row>

      <Form.Item
        label="商品數量(pcs)"
        name="estimatedQty"
        labelAlign="left"
        dependencies={["stock"]}
        rules={[
          { required: true, message: "" },
          {
            validator(_, value) {
              const maxQty = form.getFieldValue("stock") || 0;
              if (value && value > maxQty) {
                return Promise.reject(new Error("商品數量超過庫存量，請修正"));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input type="number" />
      </Form.Item>

      <Form.Item
        label={
          <span>
            單品總金額
            <Tooltip placement="bottom" title="此金額需要為整數，且單品總金額加總要等於訂單總金額。">
              <InfoIcon style={{ transform: "scale(0.9)" }} />
            </Tooltip>
          </span>
        }
        name="unitPrice"
        labelAlign="left"
        rules={[{ required: !isB2B, message: "" }]}
      >
        {isB2B ? (
          "-"
        ) : (
          <InputNumber
            style={{ width: "230px" }}
            min={0}
            precision={0}
            onKeyPress={(event) => {
              // 只允許數字 (0-9)，禁止點號 (.) 或其他字元
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
          />
        )}
      </Form.Item>
    </>
  );
}
